import { Controller } from "@hotwired/stimulus"
import { scrollContainerFor } from "../utils/scroll_container"
import { activeTocLink, parseTocPage } from "../utils/toc_spy"

// How far into the viewport a section must rise before it becomes current. Measured
// from the visible top, or from the preview banner when that chrome is on screen.
const TOC_SPY_LINE = 100
const TOC_SPY_GAP = 20

// Drives the learner lesson reader: Content/Materials tab switching, the
// per-section "Hide Transcript" toggle, and the sidebar Table of Contents
// (collapsible document sections + scroll-spy active highlight).
export default class extends Controller {
  static targets = ["tab", "panel", "formatTag", "formatSections"]

  connect() {
    this.setupTocSpy()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll, { capture: true })
    window.removeEventListener("wheel", this.onScrollGesture)
    window.removeEventListener("touchmove", this.onScrollGesture)
    this.element.removeEventListener("article-pager:page-changed", this.onRemotePage)
    this.element.removeEventListener("pdf-flipbook:page-changed", this.onRemotePage)
    if (this.spyFrame) cancelAnimationFrame(this.spyFrame)
  }

  // Fired by the hero "Choose your format" switcher (audio-versions#select /
  // #connect) whenever the active pane changes. Keeps the sidebar ToC's
  // lesson-row format tag and the Web Article's jump-links in sync with
  // whichever format is actually on screen, without a page reload.
  //
  // audio-versions:change bubbles, so a body block reusing the same
  // controller (a second document or audio card further down the lesson)
  // would fire this too — ignore anything that didn't come from the hero
  // switcher.
  onFormatChange(event) {
    if (!event.target.closest('[data-format-switcher="hero"]')) return

    const { group, label, key } = event.detail || {}
    this.updateFormatTag(group, label)
    this.updateArticleSections(key)
  }

  updateFormatTag(group, label) {
    const tag = this.formatTagTargets[0]
    if (!tag) return

    if (group === "audio" || group === "text" || group === "file") {
      tag.hidden = false
      tag.dataset.currentGroup = group
      const verb = group === "audio" ? "Listening" : "Reading"
      tag.textContent = `${verb} · ${label}`
    } else {
      tag.hidden = true
      delete tag.dataset.currentGroup
    }
  }

  updateArticleSections(key) {
    const sections = this.formatSectionsTargets[0]
    if (!sections) return

    // Jump-links point at Web Article headings, or at PDF pages when Reading
    // view is original-file. Summary and audio have nothing to follow.
    sections.hidden = key !== "full_text" && key !== "original_file"
    this.scheduleActiveToc()
  }

  // Collapses/expands the document sections nested under a TOC entry.
  toggleSection(event) {
    const button = event.currentTarget
    const entry = button.closest(".academy-toc-entry")
    if (!entry) return

    const collapsed = entry.classList.toggle("is-collapsed")
    button.setAttribute("aria-expanded", String(!collapsed))
  }

  // Highlights the TOC link whose target section is currently in view. Position
  // is read off getBoundingClientRect so zero-height document anchors (empty
  // spans bound to a non-header block) still participate — IntersectionObserver
  // never fires for a 0×0 box, which is why some courses' highlight stuck.
  setupTocSpy() {
    this.tocLinks = Array.from(
      this.element.querySelectorAll(".academy-toc-entry-link, .academy-toc-section-link")
    ).filter((link) => link.matches("a[href^='#']"))
    if (this.tocLinks.length === 0) return

    this.pinnedLink = null
    this.tocHasScrolled = false
    this.onScroll = () => {
      this.tocHasScrolled = true
      this.scheduleActiveToc()
    }
    // Capture phase: in the admin preview the page scroll belongs to `.main-content`,
    // and an element's scroll event does not bubble to window.
    window.addEventListener("scroll", this.onScroll, { passive: true, capture: true })
    this.onScrollGesture = () => { this.pinnedLink = null }
    window.addEventListener("wheel", this.onScrollGesture, { passive: true })
    window.addEventListener("touchmove", this.onScrollGesture, { passive: true })
    this.onRemotePage = (event) => {
      if (!this.eventIsForThisSpy(event)) return
      this.scheduleActiveToc()
    }
    this.element.addEventListener("article-pager:page-changed", this.onRemotePage)
    this.element.addEventListener("pdf-flipbook:page-changed", this.onRemotePage)
    this.updateActiveToc()
  }

  // Clicking a TOC link IS the choice of section, so hold it current until the
  // next scroll gesture: the last sections sit too close to the page bottom to
  // ever reach the trigger line, and the spy would otherwise light up a section
  // the reader did not pick.
  pinClickedLink(event) {
    const link = event.currentTarget
    if (!this.tocLinks?.includes(link)) return

    if (this.activateOwningPane(link) === "flipbook") event.preventDefault()
    this.pinnedLink = link
    this.markCurrentLink(link)
  }

  // A TOC link can point into a hero format pane (Audio / Web Article, or one
  // of the Web Article's own section anchors) that is currently hidden behind
  // the "Choose your format" switcher. Switch that pane in before scrolling,
  // by delegating to the same tab the switcher itself uses, so playback state
  // (pausing the outgoing pane) stays owned by the audio-versions controller.
  //
  // When Reading view is original-file the heading is not in the DOM; turn the
  // visible flipbook to the bound page instead of chasing a missing id.
  activateOwningPane(link) {
    const id = (link.getAttribute("href") || "").replace(/^#/, "")
    const target = id && document.getElementById(id)
    if (target) {
      const pane = target.closest(".academy-reader-format-pane")
      if (!pane || !pane.hasAttribute("hidden")) return null

      const section = pane.closest('[data-controller~="audio-versions"]')
      if (!section) return null

      const tab = section.querySelector(
        `[data-audio-versions-target="tab"][data-index="${pane.dataset.index}"]`
      )
      if (tab) tab.click()
      return "pane"
    }

    const page = parseTocPage(link.dataset.tocPage)
    if (page == null || page === "unpaged") return null

    const flipbook = this.visibleFlipbook()
    if (!flipbook) return null

    flipbook.dispatchEvent(new CustomEvent("pdf-flipbook:turn-to", {
      bubbles: true,
      detail: { page: Number(page) }
    }))
    return "flipbook"
  }

  scheduleActiveToc() {
    if (this.spyFrame) return
    this.spyFrame = requestAnimationFrame(() => {
      this.spyFrame = null
      this.updateActiveToc()
    })
  }

  spyLine() {
    const banner = document.querySelector(".academy-preview-banner")
    if (banner) return banner.getBoundingClientRect().bottom + TOC_SPY_GAP

    return TOC_SPY_LINE
  }

  scrollerAtBottom(scroller) {
    return scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2
  }

  // Headings are re-resolved every tick: a paginated article swaps the page's
  // blocks, so a node captured on connect is gone by page 2.
  updateActiveToc() {
    if (!this.tocLinks?.length) return
    if (this.pinnedLink) return this.markCurrentLink(this.pinnedLink)

    const scroller = scrollContainerFor(this.element)
    const current = activeTocLink({
      entries: this.tocEntries(),
      spyLine: this.spyLine(),
      atBottom: this.scrollerAtBottom(scroller),
      currentPage: this.currentPage()
    })
    if (current) this.markCurrentLink(current)
  }

  tocEntries() {
    return this.tocLinks.map((link) => {
      const id = (link.getAttribute("href") || "").replace(/^#/, "")
      return {
        link,
        heading: id ? document.getElementById(id) : null,
        page: parseTocPage(link.dataset.tocPage)
      }
    })
  }

  currentPage() {
    const pager = this.visibleElement("[data-controller~='article-pager']")
    const pagerPage = parseTocPage(pager?.dataset.articlePagerPage)
    if (pagerPage != null) return pagerPage

    const flipbook = this.visibleElement("[data-controller~='pdf-flipbook']")
    return parseTocPage(flipbook?.dataset.pdfFlipbookPage)
  }

  visibleElement(selector) {
    return Array.from(this.element.querySelectorAll(selector)).find((element) => {
      return !element.closest(".academy-reader-format-pane")?.hasAttribute("hidden")
    })
  }

  visibleFormatRoot() {
    const panes = this.element.querySelectorAll(".academy-reader-format-pane")
    if (panes.length === 0) return this.element

    return Array.from(panes).find((pane) => !pane.hasAttribute("hidden")) || this.element
  }

  visibleFlipbook() {
    return this.visibleFormatRoot().querySelector("[data-controller~='pdf-flipbook']")
  }

  eventIsForThisSpy(event) {
    const source = event.target
    if (!(source instanceof Element)) return false
    if (!this.element.contains(source)) return false

    return !source.closest(".academy-reader-format-pane")?.hasAttribute("hidden")
  }

  markCurrentLink(link) {
    this.tocLinks.forEach((other) => other.classList.remove("is-active"))
    link.classList.add("is-active")
    // Nested sections start collapsed. Expanding on the initial connect paint
    // would open the rail before the reader has scrolled; wait for a scroll or
    // a click so the existing collapsed-on-load contract stays intact.
    if (this.tocHasScrolled || this.pinnedLink === link) this.revealActiveLink(link)
  }

  revealActiveLink(link) {
    if (link.classList.contains("academy-toc-section-link")) {
      const entry = link.closest(".academy-toc-entry")
      if (entry?.classList.contains("is-collapsed")) {
        entry.classList.remove("is-collapsed")
        const button = entry.querySelector(".academy-toc-entry-toggle")
        if (button) button.setAttribute("aria-expanded", "true")
      }
    }

    this.scrollRailToLink(link)
  }

  // Only move the rail's own scroller — scrollIntoView on a link that is already
  // on screen would walk up to the page and fight the reader's position.
  scrollRailToLink(link) {
    const rail = link.closest(".academy-toc")
    if (!rail) return

    const railBox = rail.getBoundingClientRect()
    const linkBox = link.getBoundingClientRect()
    if (linkBox.top >= railBox.top && linkBox.bottom <= railBox.bottom) return

    link.scrollIntoView({ block: "nearest" })
  }

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab

    this.tabTargets.forEach((element) => {
      element.classList.toggle("is-active", element.dataset.tab === tab)
    })

    this.panelTargets.forEach((element) => {
      element.hidden = element.dataset.tab !== tab
    })
  }

  toggleTranscript(event) {
    const button = event.currentTarget
    const section = button.closest(".academy-transcript")
    if (!section) return

    const body = section.querySelector(".academy-transcript-body")
    if (!body) return

    const label = button.querySelector("[data-academy-lesson-target='transcriptToggleLabel']")
    const icon = button.querySelector("i")
    const willOpen = body.hasAttribute("hidden")
    const hideLabel = button.dataset.hideLabel || "Hide Transcript"
    const showLabel = button.dataset.showLabel || "Show Transcript"

    if (willOpen) {
      body.removeAttribute("hidden")
      section.classList.remove("is-collapsed")
      button.setAttribute("aria-expanded", "true")
      if (label) label.textContent = hideLabel
      if (icon) icon.className = "ph ph-eye-slash"
    } else {
      body.setAttribute("hidden", "")
      section.classList.add("is-collapsed")
      button.setAttribute("aria-expanded", "false")
      if (label) label.textContent = showLabel
      if (icon) icon.className = "ph ph-eye"
    }

    // The synced transcript's scroll-into-view logic skips a hidden body
    // (zero clientHeight) and then no-ops on an unchanged active segment, so
    // opening mid-playback would otherwise leave the highlight scrolled out
    // of view until the next segment boundary. Let audio-content-player
    // re-run its keep-in-view scroll now that the body is visible.
    section.dispatchEvent(
      new CustomEvent("academy-lesson:transcript-toggle", { bubbles: true, detail: { open: willOpen } })
    )
  }
}
